import React, { memo, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Checkbox from '@rsos/components/capstone/base/Checkbox';
import { gspx } from '@rsos/gainsight';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import {
  GISServiceTypes,
  GISStateLayer,
  gisLayerFor,
  gisSublayerFor,
} from '@rsos/reduxDataStore';
import { updateLayer } from '@rsos/reduxDataStore';
import { selectMapProvider } from '@rsos/sinatra';
import useIsIRP from '@rsos/utils/useIsIRP';
import { trackToggleSupplementalLayer } from '../../JurisdictionView/Map/gainsight/gisTrackings';
import { GISLayerAccordion } from './GISLayerAccordion';
import { RegularCheckboxWrapper } from './GISLayerControls.styles';
import { GISSublayerControl } from './GISSublayerControl';

interface GISLayerControlProps {
  layer: GISStateLayer;
}

/**
 * A checkbox for one GIS layer; expandable accordion for layers with sublayers.
 * Directly toggles visibility on the ArcGIS layer for immediate feedback,
 * then syncs Redux state.
 */
export const GISLayerControl = memo<GISLayerControlProps>(({ layer }) => {
  const isDisabled = useSelector(selectMapProvider) !== ESRI;
  const dispatch = useDispatch();
  const { irpVersion } = useIsIRP();

  // The basemap is controlled in the lower-left "basemap menu"; only show it
  // here if it has toggleable sublayers.
  const isBasemapWithoutSublayers =
    layer.service.service_type === GISServiceTypes.basemap &&
    layer.sublayers.length <= 1;

  const toggle = useCallback(() => {
    // Use ArcGIS layer as single source of truth for visibility
    const gisLayer = gisLayerFor(layer);
    if (!gisLayer) return;

    // Read current visibility from the layer itself, not Redux
    const newVisible = !gisLayer.visible;
    gisLayer.visible = newVisible;

    // Toggle all sublayers visibility to match
    layer.sublayers.forEach(sl => {
      const gisSublayer = gisSublayerFor(sl);
      if (gisSublayer) {
        gisSublayer.visible = newVisible;
      }
    });

    // Sync Redux state to match
    dispatch(
      updateLayer({
        layerID: layer.id,
        active: newVisible,
      }),
    );

    // Analytics tracking
    const onOffText = newVisible ? 'on' : 'off';
    gspx.trackCustomEvent('Data Map', {
      irpVersion,
      name: `Turn ${onOffText} ${layer.title}`,
      Category: 'GIS Service',
      'Launched date': new Date(),
    });

    trackToggleSupplementalLayer({
      layerName: layer.title,
      isVisible: newVisible,
      layerCategory: 'gis_service',
    });
  }, [dispatch, layer, irpVersion]);

  const sublayerControls = useMemo(
    () =>
      layer.sublayers.map(sl => (
        <GISSublayerControl
          layerId={layer.id}
          sublayer={sl}
          key={sl.uniqueId}
        />
      )),
    [layer.id, layer.sublayers],
  );

  if (isBasemapWithoutSublayers) {
    return null;
  }

  if (layer.sublayers.length > 1) {
    return (
      <GISLayerAccordion
        title={
          <Checkbox
            label={layer.title}
            isChecked={layer.active}
            disabled={isDisabled}
            onChange={toggle}
          />
        }
        body={<>{sublayerControls}</>}
      />
    );
  }

  return (
    <RegularCheckboxWrapper>
      <Checkbox
        label={layer.title}
        isChecked={layer.active}
        disabled={isDisabled}
        onChange={toggle}
      />
    </RegularCheckboxWrapper>
  );
});
